import axios, { AxiosError } from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";

import { API_URL } from "@/constants/CEnv";

import {
	PLAN_LIMIT_CODE,
	PLAN_LIMIT_EVENT,
	TPlanLimitDetails,
} from "@/types/TSubscription";

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const publicClient = axios.create({
	baseURL: API_URL,
	headers: {
		"Content-Type": "application/json",
		"X-Timezone": timezone,
	},
});

const client = axios.create({
	baseURL: API_URL,
	headers: {
		"Content-Type": "application/json",
		"X-Timezone": timezone,
	},
});

/**
 * The access token lives here and nowhere else.
 *
 * It used to be written to localStorage or sessionStorage, which any script
 * running on the page - an injected payload, a compromised dependency - could
 * read and exfiltrate. Held in a module variable it dies with the tab, and a
 * page reload gets a new one from the refresh cookie, which is HttpOnly and
 * out of reach of JavaScript entirely.
 */
let accessToken: string | null = null;

type TokenListener = (token: string | null) => void;

const tokenListeners = new Set<TokenListener>();

/**
 * Lets React state follow a token that changed outside React - the refresh
 * interceptor renews it from inside an axios call, with no component involved.
 */
export const onTokenChange = (listener: TokenListener): (() => void) => {
	tokenListeners.add(listener);

	return () => {
		tokenListeners.delete(listener);
	};
};

export const getAccessToken = (): string | null => accessToken;

/**
 * Sets the access token, or clears it when called with no argument.
 *
 * The previous version ignored a falsy value, so there was no way to drop the
 * Authorization header on logout: the old token stayed on the axios defaults
 * and went out with the next request.
 */
export const setHeaderToken = (token?: string | null): void => {
	accessToken = token ?? null;

	if (accessToken) {
		client.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
	} else {
		delete client.defaults.headers.common.Authorization;
	}

	tokenListeners.forEach((listener) => listener(accessToken));
};

const REFRESH_LOCK = "chronos:refresh-token";

/**
 * Runs a refresh-token exchange alone across every tab of the browser. The
 * refresh token is single-use: two tabs exchanging the same cookie at once
 * looked like a replay to the API. The tab that waited runs after, with the
 * cookie the first one rotated. Browsers without the Web Locks API just run
 * it (the API forgives a retry shortly after anyway).
 */
export const withRefreshLock = <T>(run: () => Promise<T>): Promise<T> =>
	typeof navigator !== "undefined" && navigator.locks
		? navigator.locks.request(REFRESH_LOCK, run)
		: run();

/**
 * The API turned the refresh token down (spent, expired, revoked): the
 * session is over. Anything else — no response at all (offline, a dropped
 * connection), a 5xx — says nothing about the session and must not sign
 * the user out.
 */
export const isRefreshRefused = (error: unknown): boolean => {
	const status = (error as AxiosError | undefined)?.response?.status;
	return status === 400 || status === 401;
};

const refreshAuth = async (failedRequest: AxiosError) => {
	try {
		const newAuthData = await withRefreshLock(() =>
			publicClient
				.post(
					"security/refresh-token",
					{},
					{
						headers: { "X-Timezone": timezone },
						withCredentials: true,
					}
				)
				.then((res) => res.data)
		);

		if (!newAuthData?.access_token) {
			return Promise.reject(new Error("Refresh token failed"));
		}

		setHeaderToken(newAuthData.access_token);

		if (failedRequest.response) {
			failedRequest.response.config.headers.Authorization = `Bearer ${newAuthData.access_token}`;
		}

		return Promise.resolve(newAuthData);
	} catch (error) {
		// A network blip is not a logout: the request fails, the token stays,
		// and the next call refreshes again once the connection is back.
		if (isRefreshRefused(error)) {
			setHeaderToken(null);
			window.dispatchEvent(new Event("auth:logout"));
		}

		return Promise.reject(new Error("Failed to refresh token"));
	}
};

client.interceptors.request.use((config) => {
	if (accessToken && config.url !== "security/refresh-token") {
		config.headers.Authorization = `Bearer ${accessToken}`;
	}

	return config;
});

/**
 * A `402 PLAN.LIMIT` is the API refusing something the plan does not
 * allow. Every caller gets its rejection as usual (a form stays open, a
 * toast may show the message), and the page-level UpgradeModal, which
 * listens for this event, offers the plan that would lift the limit -
 * one place for the upgrade prompt rather than one per creation form.
 */
export const isPlanLimitError = (error: unknown): error is AxiosError => {
	const response = (error as AxiosError<{ code?: string }>)?.response;
	return response?.status === 402 && response.data?.code === PLAN_LIMIT_CODE;
};

client.interceptors.response.use(undefined, (error: unknown) => {
	if (isPlanLimitError(error)) {
		const details = (
			error.response?.data as { details?: TPlanLimitDetails } | undefined
		)?.details;
		if (details) {
			window.dispatchEvent(
				new CustomEvent<TPlanLimitDetails>(PLAN_LIMIT_EVENT, {
					detail: details,
				})
			);
		}
	}
	return Promise.reject(error);
});

// The refresh endpoint reads its token from the HttpOnly cookie, so every
// call that might trigger a refresh has to carry credentials.
client.defaults.withCredentials = true;
publicClient.defaults.withCredentials = true;

// axios-auth-refresh 5 queues the instance's other requests while a refresh
// is in flight by default (v3's pauseInstanceWhileRefreshing), and gives up
// after three consecutive refreshes so a refresh that succeeds while the
// retried request keeps answering 401 cannot loop forever.
createAuthRefreshInterceptor(client, refreshAuth, {
	statusCodes: [401],
	shouldRefresh: (failedRequest: AxiosError) => {
		const excludedUrls = ["auth/login", "security/refresh-token"];
		if (failedRequest.response?.status !== 401) return false;

		return !excludedUrls.includes(failedRequest?.config?.url || "unknown");
	},
});

export default client;
